import type { MessageGeneration, MessageToolCall, StreamTodoItem, StreamTodoStatus, ToolCallStreamStatus } from '../types/chat';

/**
 * Maps API `metadata.toolCalls` onto {@link MessageToolCall} for UI and store state.
 */
export function parseToolCallsFromMetadata(metadata: unknown): MessageToolCall[] | undefined {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }
  const raw = (metadata as { toolCalls?: unknown }).toolCalls;
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }

  const parsed: MessageToolCall[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const o = item as Record<string, unknown>;
    const toolId = String(o.toolId ?? o.ToolId ?? '');
    const toolName = String(o.toolName ?? o.ToolName ?? '');
    if (!toolId && !toolName) {
      continue;
    }

    let args: Record<string, unknown> | undefined;
    const rawArgs = o.arguments ?? o.Arguments;
    if (rawArgs && typeof rawArgs === 'object' && !Array.isArray(rawArgs)) {
      args = rawArgs as Record<string, unknown>;
    }

    parsed.push({
      toolId: toolId || toolName,
      toolName: toolName || toolId,
      ...(args && { arguments: args }),
      resultSummary:
        o.resultSummary != null
          ? String(o.resultSummary)
          : o.ResultSummary != null
            ? String(o.ResultSummary)
            : undefined,
      status: o.status != null ? String(o.status) : o.Status != null ? String(o.Status) : undefined,
      error: o.error != null ? String(o.error) : o.Error != null ? String(o.Error) : null,
      durationMs:
        typeof o.durationMs === 'number'
          ? o.durationMs
          : typeof o.DurationMs === 'number'
            ? o.DurationMs
            : undefined,
    });
  }

  return parsed.length > 0 ? parsed : undefined;
}

function hasStreamToolFields(record: Record<string, unknown>): boolean {
  return (
    record.tool_name != null ||
    record.toolName != null ||
    record.tool_call_id != null ||
    record.tool_id != null ||
    record.toolId != null ||
    record.args != null ||
    record.arguments != null ||
    record.raw_tool_name != null
  );
}

function normalizeStreamToolCallPayload(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  let current = data as Record<string, unknown>;

  // Peel envelopes like streamingResponse.data.data.args (double-nested `data`).
  for (let depth = 0; depth < 4; depth += 1) {
    if (hasStreamToolFields(current)) {
      return current;
    }

    const nested = current.data;
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
      break;
    }

    const inner = nested as Record<string, unknown>;
    if (hasStreamToolFields(inner)) {
      return {
        ...current,
        ...inner,
        tool_call_id:
          inner.tool_call_id ??
          inner.tool_id ??
          inner.toolId ??
          current.tool_call_id ??
          current.tool_id ??
          current.toolId,
        tool_name:
          inner.tool_name ??
          inner.toolName ??
          current.tool_name ??
          current.toolName,
        raw_tool_name: inner.raw_tool_name ?? current.raw_tool_name,
        args: inner.args ?? inner.arguments ?? current.args ?? current.arguments,
      };
    }

    current = inner;
  }

  return current;
}

function parseStreamToolCallBase(data: unknown): Pick<MessageToolCall, 'toolId' | 'toolName'> | null {
  const o = normalizeStreamToolCallPayload(data);
  if (!o) {
    return null;
  }
  const toolId = String(o.tool_call_id ?? o.tool_id ?? o.toolId ?? '').trim();
  const toolName = String(o.tool_name ?? o.toolName ?? '').trim();
  if (!toolId && !toolName) {
    return null;
  }
  return {
    toolId: toolId || toolName,
    toolName: toolName || toolId,
  };
}

function parseStreamToolArguments(data: Record<string, unknown>): Record<string, unknown> | undefined {
  const rawArgs = data.arguments ?? data.Arguments ?? data.args;
  if (rawArgs && typeof rawArgs === 'object' && !Array.isArray(rawArgs)) {
    return rawArgs as Record<string, unknown>;
  }
  if (typeof rawArgs === 'string' && rawArgs.trim()) {
    try {
      const parsed = JSON.parse(rawArgs) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // ignore malformed JSON args
    }
  }
  return undefined;
}

function parseStreamToolDurationMs(data: Record<string, unknown>): number | undefined {
  const rawDuration = data.duration_ms ?? data.durationMs;
  if (typeof rawDuration === 'number' && Number.isFinite(rawDuration)) {
    return rawDuration;
  }
  if (typeof rawDuration === 'string') {
    const parsed = Number.parseFloat(rawDuration);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function normalizeTodoStatus(status: string): StreamTodoStatus {
  const value = status.toLowerCase();
  if (value === 'completed' || value === 'done') {
    return 'completed';
  }
  if (
    value === 'in_progress' ||
    value === 'in-progress' ||
    value === 'active' ||
    value === 'running'
  ) {
    return 'in_progress';
  }
  return 'pending';
}

/** Parses `args.todos` from write_todos / Update Todos stream payloads. */
export function parseStreamTodos(
  args?: Record<string, unknown>,
): StreamTodoItem[] | undefined {
  const raw = args?.todos;
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }

  const items: StreamTodoItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const content = String(row.content ?? row.title ?? '').trim();
    if (!content) {
      continue;
    }
    items.push({
      content,
      status: normalizeTodoStatus(String(row.status ?? 'pending')),
    });
  }

  return items.length > 0 ? items : undefined;
}

/**
 * Returns a new array where any todo with status `in_progress` is updated to `completed`.
 * Used when END_OF_STREAM is detected so the last active todo is struck through/marked completed.
 */
export function completeInProgressTodos(
  todos?: StreamTodoItem[]
): StreamTodoItem[] | undefined {
  if (!todos?.length) {
    return todos;
  }
  return todos.map((todo) =>
    todo.status === 'in_progress' ? { ...todo, status: 'completed' } : todo
  );
}

/**
 * Returns a new array of tool calls where any write_todos tool call with in_progress todos
 * has those items updated to completed.
 */
export function completeInProgressToolCalls(
  toolCalls?: MessageToolCall[]
): MessageToolCall[] | undefined {
  if (!toolCalls?.length) {
    return toolCalls;
  }
  return toolCalls.map((call) => {
    const rawTodos = call.arguments?.todos;
    if (!Array.isArray(rawTodos) || rawTodos.length === 0) {
      return call;
    }
    let hasChanged = false;
    const nextTodos = rawTodos.map((item) => {
      if (
        item &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        (item as Record<string, unknown>).status === 'in_progress'
      ) {
        hasChanged = true;
        return { ...(item as Record<string, unknown>), status: 'completed' };
      }
      return item;
    });
    if (!hasChanged) {
      return call;
    }
    return {
      ...call,
      arguments: {
        ...call.arguments,
        todos: nextTodos,
      },
    };
  });
}

export function isWriteTodosTool(toolEntry: Pick<MessageToolCall, 'toolName'>): boolean {
  const name = toolEntry.toolName.toLowerCase();
  return (
    name.includes('update todos') ||
    name.includes('write_todos') ||
    name.includes('write todos')
  );
}

/** WebSocket TOOL_CALL_STARTED payload. */
export function parseToolCallFromStreamStartedPayload(data: unknown): MessageToolCall | null {
  const o = normalizeStreamToolCallPayload(data);
  const base = parseStreamToolCallBase(data);
  if (!base || !o) {
    return null;
  }
  const args = parseStreamToolArguments(o);
  return {
    ...base,
    ...(args && { arguments: args }),
    status: 'running',
  };
}

/** WebSocket TOOL_CALL_COMPLETED payload. */
export function parseToolCallFromStreamCompletedPayload(data: unknown): MessageToolCall | null {
  const o = normalizeStreamToolCallPayload(data);
  const base = parseStreamToolCallBase(data);
  if (!base || !o) {
    return null;
  }
  const durationMs = parseStreamToolDurationMs(o);
  const args = parseStreamToolArguments(o);
  const error =
    o.error != null ? String(o.error) : o.Error != null ? String(o.Error) : null;
  const status =
    error != null
      ? 'error'
      : o.status != null
        ? String(o.status)
        : o.Status != null
          ? String(o.Status)
          : 'success';

  return {
    ...base,
    ...(args && { arguments: args }),
    ...(durationMs != null ? { durationMs } : {}),
    status,
    error,
  };
}

export function upsertMessageToolCall(
  calls: MessageToolCall[] | undefined,
  next: MessageToolCall
): MessageToolCall[] {
  const list = calls ? [...calls] : [];
  const index = list.findIndex((call) => call.toolId === next.toolId);
  if (index >= 0) {
    list[index] = { ...list[index], ...next };
    return list;
  }
  return [...list, next];
}

export function getToolCallStreamStatusLabel(
  status: ToolCallStreamStatus,
  toolName?: string
): string {
  const name = toolName?.trim();
  if (status === 'started') {
    return name ? `Tool call started for ${name}` : 'Tool call started';
  }
  return name ? `Tool call finished for ${name}` : 'Tool call finished';
}

export function shouldRenderToolCallStreamStatus(generation?: Pick<
  MessageGeneration,
  'content' | 'toolCallStreamStatus'
>): boolean {
  if (!generation?.toolCallStreamStatus) {
    return false;
  }
  return !generation?.content?.trim();
}

export function clearToolCallStreamStatusFields<T extends MessageGeneration>(generation: T): T {
  const { toolCallStreamStatus: _s, activeToolCallName: _n, ...rest } = generation;
  return rest as T;
}

/** Aggregated tool call row for UI (same {@link MessageToolCall.toolId} merged). */
export interface GroupedMessageToolCall {
  toolId: string;
  toolName: string;
  count: number;
  totalDurationMs?: number;
}

/**
 * Groups tool calls by {@link MessageToolCall.toolId}, summing durations and counting repeats.
 */
export function groupMessageToolCallsByToolId(toolCalls: MessageToolCall[]): GroupedMessageToolCall[] {
  const grouped = new Map<string, GroupedMessageToolCall>();

  for (const call of toolCalls) {
    const key = call.toolId || call.toolName;
    const existing = grouped.get(key);

    if (existing) {
      existing.count += 1;
      if (call.durationMs != null) {
        existing.totalDurationMs = (existing.totalDurationMs ?? 0) + call.durationMs;
      }
      continue;
    }

    grouped.set(key, {
      toolId: key,
      toolName: call.toolName || call.toolId,
      count: 1,
      ...(call.durationMs != null && { totalDurationMs: call.durationMs }),
    });
  }

  return Array.from(grouped.values());
}
